import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface WaitingListEntry {
  id: string;
  userId: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  reason?: string;
  createdAt: Timestamp;
  label?: string;
}

export interface WaitingListMetrics {
  totalEntries: number;
  entriesByState: { [key: string]: number };
  entriesByCity: { [key: string]: number };
  entriesByMonth: { [key: string]: number };
  recentEntries: WaitingListEntry[];
  geographicClusters: {
    center: { lat: number; lng: number };
    count: number;
    entries: WaitingListEntry[];
  }[];
  growthTrend: {
    date: string;
    count: number;
  }[];
}

/**
 * Calculate distance between two coordinates using Haversine formula
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Group entries into geographic clusters
 */
function clusterEntries(entries: WaitingListEntry[], clusterRadiusKm: number = 10): WaitingListMetrics['geographicClusters'] {
  const clusters: WaitingListMetrics['geographicClusters'] = [];
  const processed = new Set<string>();

  entries.forEach(entry => {
    if (processed.has(entry.id) || !entry.latitude || !entry.longitude) {
      return;
    }

    const cluster: WaitingListMetrics['geographicClusters'][0] = {
      center: { lat: entry.latitude, lng: entry.longitude },
      count: 1,
      entries: [entry],
    };

    processed.add(entry.id);

    // Find nearby entries
    entries.forEach(otherEntry => {
      if (
        processed.has(otherEntry.id) ||
        !otherEntry.latitude ||
        !otherEntry.longitude
      ) {
        return;
      }

      const distance = haversineDistance(
        entry.latitude!,
        entry.longitude!,
        otherEntry.latitude!,
        otherEntry.longitude!
      );

      if (distance <= clusterRadiusKm) {
        cluster.entries.push(otherEntry);
        processed.add(otherEntry.id);
        cluster.count++;
        
        // Update cluster center (average of all points)
        const avgLat = cluster.entries.reduce((sum, e) => sum + (e.latitude || 0), 0) / cluster.entries.length;
        const avgLng = cluster.entries.reduce((sum, e) => sum + (e.longitude || 0), 0) / cluster.entries.length;
        cluster.center = { lat: avgLat, lng: avgLng };
      }
    });

    clusters.push(cluster);
  });

  return clusters.sort((a, b) => b.count - a.count);
}

/**
 * Get all waiting list entries
 */
export async function getAllWaitingListEntries(): Promise<WaitingListEntry[]> {
  try {
    const waitingListRef = collection(db, 'waitingList');
    const q = query(waitingListRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as WaitingListEntry[];
  } catch (error) {
    console.error('Error fetching waiting list entries:', error);
    throw error;
  }
}

/**
 * Get waiting list metrics and analytics
 */
export async function getWaitingListMetrics(): Promise<WaitingListMetrics> {
  try {
    const entries = await getAllWaitingListEntries();

    // Basic counts
    const totalEntries = entries.length;

    // Group by state
    const entriesByState: { [key: string]: number } = {};
    entries.forEach(entry => {
      const state = entry.state || 'Unknown';
      entriesByState[state] = (entriesByState[state] || 0) + 1;
    });

    // Group by city
    const entriesByCity: { [key: string]: number } = {};
    entries.forEach(entry => {
      const city = entry.city || 'Unknown';
      entriesByCity[city] = (entriesByCity[city] || 0) + 1;
    });

    // Group by month
    const entriesByMonth: { [key: string]: number } = {};
    entries.forEach(entry => {
      if (entry.createdAt) {
        const date = entry.createdAt.toDate();
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        entriesByMonth[monthKey] = (entriesByMonth[monthKey] || 0) + 1;
      }
    });

    // Recent entries (last 10)
    const recentEntries = entries.slice(0, 10);

    // Geographic clusters
    const geographicClusters = clusterEntries(entries, 10);

    // Growth trend (last 6 months)
    const growthTrend: { date: string; count: number }[] = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      growthTrend.push({
        date: monthKey,
        count: entriesByMonth[monthKey] || 0,
      });
    }

    return {
      totalEntries,
      entriesByState,
      entriesByCity,
      entriesByMonth,
      recentEntries,
      geographicClusters,
      growthTrend,
    };
  } catch (error) {
    console.error('Error calculating waiting list metrics:', error);
    throw error;
  }
}

/**
 * Get entries within a geographic area (for map filtering)
 */
export async function getWaitingListEntriesInBounds(
  north: number,
  south: number,
  east: number,
  west: number
): Promise<WaitingListEntry[]> {
  try {
    const entries = await getAllWaitingListEntries();
    
    return entries.filter(entry => {
      if (!entry.latitude || !entry.longitude) return false;
      return (
        entry.latitude >= south &&
        entry.latitude <= north &&
        entry.longitude >= west &&
        entry.longitude <= east
      );
    });
  } catch (error) {
    console.error('Error fetching entries in bounds:', error);
    throw error;
  }
}

